import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, Type } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { TableFilters } from '../../shared/components/table/models/page.model';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  dialogService = inject(DialogService);
  constructQueryParam<T>(query: string, values: T[]): string {
    if (!values.length) {
      return '';
    }
    const queryString = values.map((id) => `${query}=${encodeURIComponent(id as string)}`).join('&');
    return `?${queryString}`;
  }

  getFileNameFromHeaders(blob: HttpResponse<Blob>) {
    const contentDisposition = blob.headers.get('content-disposition') || '';
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    let fileName = 'download.xlsx';
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }

    return fileName;
  }

  // used in resolvers
  async checkPreRequisites(conditionFn: () => boolean, interval: number = 50): Promise<void> {
    return new Promise((resolve) => {
      const checkCondition = () => {
        if (conditionFn()) {
          resolve();
        } else {
          setTimeout(checkCondition, interval);
        }
      };
      checkCondition();
    });
  }

  openComponentModal<T, K = undefined>(params: { component: Type<T>; header?: string; width?: string; data?: K }) {
    this.dialogService.open(params.component, {
      /** PrimeNG dialog bug workaround - focusOnShow set to false */
      focusOnShow: false,
      // focusTrap: true,
      dismissableMask: true,
      header: params?.header || '',
      width: params?.width || '50%',
      closable: true,
      contentStyle: { padding: 0 },
      data: params?.data || null
    });
  }

  /**
   * Converts start date/end date to local time 00:00:00/23:59:59 then
   * converts to UTC when passing to payload
   */
  getDestructuredDateRange(dateRange: (string | Date)[]) {
    if (!Array.isArray(dateRange) || dateRange.length === 0) {
      return { startDate: null, endDate: null };
    }

    const [startRaw, endRaw] = dateRange;

    const toUtcISOString = (input: string | Date, hours: number, minutes: number, seconds: number): string => {
      const date = new Date(input);
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${input}`);
      }

      date.setHours(hours, minutes, seconds, 0); // local time

      return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
    };

    const startDate = startRaw ? toUtcISOString(startRaw, 0, 0, 0) : null;

    const isInvalidEnd = !endRaw || endRaw === '1970-01-01';
    const endBase = isInvalidEnd ? startRaw : endRaw;
    const endDate = endBase ? toUtcISOString(endBase, 23, 59, 59) : null;

    return { startDate, endDate };
  }

  isFutureDate(date: string): boolean {
    if (!date) return false;

    const givenDate = new Date(date);
    const today = new Date();

    givenDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return givenDate.getTime() > today.getTime();
  }

  /**
   * Extracts filter values from table
   */
  getSelectedFilterValues(tableFilters: TableFilters) {
    const filtersRecord: Record<string, string[]> = {};

    if (!('global' in tableFilters)) {
      for (const [field, filterArray] of Object.entries(tableFilters)) {
        const values = (filterArray as any[])
          .flatMap((f) => f?.value ?? []) // handles if f.value is null or undefined
          .map((v: any) => {
            if (v && typeof v === 'object' && 'value' in v && v.value === null) return 'null';
            if (v && typeof v === 'object' && 'value' in v) return v.value;
            return v;
          })
          .filter((v) => v != null);

        if (values.length > 0) {
          filtersRecord[field] = values;
        }
      }
    }

    return filtersRecord;
  }

  camelToSnake(name: string): string {
    return name.replace(/([A-Z])/g, '_$1').toLowerCase();
  }
}
